import { useQueryClient } from "@tanstack/react-query";
import { Form } from "antd";
import { useGetCascadeQuery, useUpdateCascadeMutation } from "api/TenantSlug/Cascade/hooks";
import { BaseFilter } from "components/UI/BaseFilter/BaseFilter";
import { FilterErrorsModal } from "components/UI/BaseFilter/FilterErrorsModal";
import { useFilterEditor } from "components/UI/BaseFilter/hooks/useFilterEditor";
import { useFilterFormStructures } from "components/UI/BaseFilter/hooks/useFilterFormStructures";
import { asRequestFilter } from "components/UI/BaseFilter/types/filterTree";
import { QueryState } from "components/UI/QueryState/QueryState.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useEffect, useLayoutEffect, useState } from "react";
import { toast } from "react-toastify";
import { FormId } from "types/models.ts";

interface CascadeFilterDrawerBodyProps {
    cascadeId: string;
    onDirtyChange: (isDirty: boolean) => void;
    onSavingChange: (isSaving: boolean) => void;
    /** Gets the id of the cascade that was saved, the drawer may show another one by the time the save ends */
    onSaved: (savedCascadeId: string) => void;
    onFilterSaved?: () => void | Promise<void>;
}

/**
 * Owns everything specific to one cascade's filter (query, editor state, save). Mounted keyed by
 * `cascadeId` from `CascadeFilterDrawer`, so switching to a different channel's filter — without
 * closing the drawer first — is a plain remount: no leftover state from the previous channel to
 * reconcile, no risk of showing one channel's filter under another one's id.
 */
export const CascadeFilterDrawerBody = ({
    cascadeId,
    onDirtyChange,
    onSavingChange,
    onSaved,
    onFilterSaved,
}: CascadeFilterDrawerBodyProps) => {
    const { t: tF } = usePaldockTranslation("ui.offer-filter");
    const queryClient = useQueryClient();
    const [errorsOpen, setErrorsOpen] = useState(false);

    const cascadeQuery = useGetCascadeQuery(cascadeId, { filter: { include: "integration" } });
    const cascade = cascadeQuery.data?.cascade;

    const structureId = cascade?.integration?.form_structure_id;
    const { formStructures, fieldTags } = useFilterFormStructures(structureId ? [structureId] : []);

    const editor = useFilterEditor(cascade?.filter, "cascade", fieldTags);
    const updateCascadeMutation = useUpdateCascadeMutation({
        onError: () => toast.error(tF("messages.save_error")),
    });

    // Layout effects, so the shell already sees the clean state (after `markSaved`) when `onSaved` fires,
    // even if the invalidations that come first resolve immediately
    useLayoutEffect(() => onDirtyChange(editor.isDirty), [editor.isDirty, onDirtyChange]);
    // The drawer shell outlives this body, so a closed/switched-away editor must not leave "dirty" behind
    useLayoutEffect(() => () => onDirtyChange(false), [onDirtyChange]);
    useEffect(() => onSavingChange(updateCascadeMutation.isPending), [updateCascadeMutation.isPending, onSavingChange]);

    const handleSubmit = async () => {
        // A filter the editor cannot show has an empty draft — saving it would wipe the filter on BE
        if (editor.unsupported) {
            toast.warning(tF("unsupported.message"));
            return;
        }
        if (!editor.validate()) {
            setErrorsOpen(true);
            return;
        }

        try {
            const submittedTree = editor.toTree();
            await updateCascadeMutation.mutateAsync({ request: { filter: asRequestFilter(submittedTree) }, cascadeId });
            editor.markSaved(submittedTree);
            await queryClient.invalidateQueries({ queryKey: ["getCascades"] });
            await queryClient.invalidateQueries({ queryKey: ["getCascade", cascadeId] });
            await onFilterSaved?.();
            toast.success(tF("messages.filters_saved"));
            onSaved(cascadeId);
        } catch (error) {
            console.error("Error saving cascade filter:", error);
        }
    };

    if (!cascade) {
        return (
            <QueryState isLoading={cascadeQuery.isLoading} isError={cascadeQuery.isError || !cascadeQuery.isLoading} />
        );
    }

    return (
        <Form id={FormId.CascadeFilterForm} onFinish={handleSubmit}>
            <BaseFilter editor={editor} formStructures={formStructures} />
            <FilterErrorsModal errors={editor.errors} open={errorsOpen} onClose={() => setErrorsOpen(false)} />
        </Form>
    );
};
